import React, { memo, useCallback, useRef } from 'react';
import classNames from 'classnames';
import { Page } from 'react-pdf';
import Loader from '@components/Loader';
import { hasRenderedTextLayerText } from '@pages/MediaViewer/AssetViewer/PdfCard/utils';

const pageLoader = (
  <div className="media_viewer_pdf-mobile-loader">
    <Loader spinSize={40} isSpinning={true} />
  </div>
);

type MobilePdfPageProps = {
  pageNumber: number;
  pageWidth?: number;
  pageHeight?: number;
  turnoverDegree: number;
  isActive: boolean;
  isFirstPage: boolean;
  shouldRender: boolean;
  pageCanvasRef?: React.RefObject<HTMLCanvasElement>;
  onRegisterPageRoot: (page: number, element: HTMLDivElement | null) => void;
  onFirstPageLoadSuccess: (pdfPage: {
    originalWidth: number;
    originalHeight: number;
  }) => void;
  onPageLoadSuccess?: (pageNumber: number) => void;
  onPageTextContent?: (pageNumber: number, hasText: boolean) => void;
};

function MobilePdfPage({
  pageNumber,
  pageWidth,
  pageHeight,
  turnoverDegree,
  isActive,
  isFirstPage,
  shouldRender,
  pageCanvasRef,
  onRegisterPageRoot,
  onFirstPageLoadSuccess,
  onPageLoadSuccess,
  onPageTextContent
}: MobilePdfPageProps) {
  const shouldRenderPage = shouldRender || isActive;
  const rootRef = useRef<HTMLDivElement | null>(null);

  const assignRootRef = useCallback(
    (element: HTMLDivElement | null) => {
      rootRef.current = element;
      onRegisterPageRoot(pageNumber, element);
    },
    [onRegisterPageRoot, pageNumber]
  );

  return (
    <div
      ref={assignRootRef}
      className={classNames('media_viewer_pdf-mobile-page', {
        'b-active': isActive
      })}
      style={{ minHeight: pageHeight }}
    >
      <div
        className="media_viewer_pdf-inner"
        style={{
          transform: `rotate(${turnoverDegree}deg)`,
          width: pageWidth,
          height: pageHeight
        }}
      >
        {shouldRenderPage ? (
          <Page
            width={pageWidth}
            height={pageHeight}
            scale={1}
            pageNumber={pageNumber}
            className="media_viewer_pdf-page"
            canvasRef={isActive ? pageCanvasRef : undefined}
            renderAnnotationLayer={false}
            onLoadSuccess={(pdfPage) => {
              onPageLoadSuccess?.(pageNumber);
              if (isFirstPage) onFirstPageLoadSuccess(pdfPage);
            }}
            onRenderTextLayerSuccess={() => {
              onPageTextContent?.(
                pageNumber,
                hasRenderedTextLayerText(rootRef.current)
              );
            }}
            loading={pageLoader}
          />
        ) : null}
      </div>
    </div>
  );
}

export default memo(MobilePdfPage);
